export type PropertyListingType = "auction-property" | "private-property";

interface Location {
  city_name?: string;
  state_name?: string;
}

interface AssetType {
  slug?: string | null;
}

interface PropertyData {
  title?: string;
  description?: string;
  reserve_price?: number | string;
  price?: number | string;
  location?: Location;
  asset_type?: AssetType; // auction
  sub_asset_type_details?: AssetType; // private
}

interface StructuredDataParams {
  data: PropertyData;
  propertyListingType: PropertyListingType;
  canonicalUrl: string;
  image: string;
  baseUrl: string;
}

/* -----------------------------
   Invalid & Normalization
--------------------------------*/

const INVALID_VALUES = new Set([
  "0",
  "na",
  "select",
  "ggg",
  "test",
  "test-1",
  "tester",
]);

const NORMALIZATION_MAP: Record<string, string> = {
  agricutural: "agricultural",
  agriculture: "agricultural",

  commerical: "commercial",
  commericial: "commercial",

  industial: "industrial",
  indutrial: "industrial",
  industral: "industrial",

  residentail: "residential",

  bangalow: "bungalow",
  falt: "flat",
  shpos: "shops",
  gowdown: "godown",

  machinary: "machinery",
  "plant-and-machinary": "plant-and-machinery",

  inidividual: "individual",
  individiual: "individual",
};

const normalizeSlug = (value?: string | null): string | null => {
  if (!value || typeof value !== "string") return null;

  const slug = value.toLowerCase().trim();
  if (!slug || INVALID_VALUES.has(slug)) return null;

  const cleaned = slug.replace(/-\d+$/, "");

  return NORMALIZATION_MAP[cleaned] || cleaned;
};

/* -----------------------------
   Schema Type Resolver
--------------------------------*/

const getSchemaType = (normalized: string | null): string => {
  if (!normalized) return "Product";

  // Residential
  if (
    [
      "residential",
      "flat",
      "villa",
      "bungalow",
      "independent-house",
      "individual-house",
      "residential-plot",
      "plot-residential",
    ].includes(normalized)
  )
    return "Residence";

  // Commercial
  if (
    [
      "commercial",
      "office",
      "shop",
      "shops",
      "showroom",
      "mall",
      "malls",
      "commercial-plot",
      "plot-commercial",
    ].includes(normalized)
  )
    return "CommercialBuilding";

  // Industrial
  if (
    [
      "industrial",
      "industrial-plot",
      "industrial-land",
      "industrial-shed",
      "factory",
      "godown",
      "godowns",
      "shed",
      "industrial-land-and-building",
    ].includes(normalized)
  )
    return "IndustrialFacility";

  // Agricultural / Land
  if (
    [
      "agricultural",
      "agriculture-land",
      "farm-land",
      "farm-house",
      "vacant-land",
      "land-and-building",
      "plot",
    ].includes(normalized)
  )
    return "Land";

  // Vehicle
  if (["vehicle", "car", "cars", "bus"].includes(normalized)) return "Vehicle";

  return "Product";
};

/* -----------------------------
   Main Generator
--------------------------------*/

export const generatePropertyDetailsStructuredData = ({
  data,
  propertyListingType,
  canonicalUrl,
  image,
  baseUrl,
}: StructuredDataParams) => {
  const rawSlug =
    propertyListingType === "auction-property"
      ? data?.asset_type?.slug
      : data?.sub_asset_type_details?.slug;

  const normalized = normalizeSlug(rawSlug);
  const schemaType = getSchemaType(normalized);

  const isRealEstate = [
    "Residence",
    "CommercialBuilding",
    "IndustrialFacility",
    "Land",
  ].includes(schemaType);

  const structuredData: Record<string, any> = {
    "@context": "https://schema.org",
    "@type":
      propertyListingType === "auction-property" && isRealEstate
        ? "RealEstateListing"
        : schemaType,
    name: data?.title || "Property Details",
    description: data?.description || "",
    url: canonicalUrl,
    image,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    offers: {
      "@type": "Offer",
      priceCurrency: "INR",
      price: data?.reserve_price || data?.price || undefined,
      availability: "https://schema.org/InStock",
      url: canonicalUrl,
    },
    provider: {
      "@type": "Organization",
      name: "Auctionbazaar",
      url: baseUrl,
    },
  };

  if (isRealEstate) {
    structuredData.address = {
      "@type": "PostalAddress",
      addressLocality: data?.location?.city_name || undefined,
      addressRegion: data?.location?.state_name || undefined,
      addressCountry: "IN",
    };
  }

  return structuredData;
};
